/**
 * Helper klasifikasi unit pelayanan.
 *
 * Aturan diturunkan dari docs/architecture/UNIT_CLASSIFICATION.md
 * (snapshot data c_upm: 2026-05-11). Konsisten dengan pola hardcode existing:
 *   - '01'                -> IGD / Gadar
 *   - kode < '30'         -> Ralan (Poliklinik)
 *   - '30' <= kode < '50' -> Penunjang Medis / Treatment
 *   - '50' <= kode < '80' -> Ranap (Bangsal)
 *   - kode >= '80'        -> Administrasi / Manajemen (bukan unit klinis)
 *
 * Perbandingan dilakukan sebagai string (sama dengan kode existing) sehingga
 * kode huruf-angka seperti '1A', '2H', '5A' tetap masuk ke kategori yang tepat.
 */

export type UnitType =
  | "gadar"
  | "ralan"
  | "penunjang"
  | "ranap"
  | "admin"
  | "unknown";

const GADAR = "01";

const KEMOTERAPI = "38";
const HEMODIALISA = "37";
const LABORATORIUM = "31";
const RADIOLOGI = "32";
export const BANK_DARAH = "35";
const REKAM_MEDIS = "80";
const PENDAFTARAN = "81";
const INSTALASI_GIZI = "83";
const ADMINISTRASI = "87";
const IBS = "36";
const RB_OBSTETRI = "63";
const KUNTHI = "56";

const UNIT_BERSALIN: ReadonlyArray<string> = [RB_OBSTETRI, "66"];

const UNIT_INTENSIF: ReadonlyArray<string> = [
  "50",
  "5A",
  "5B",
  "5C",
  "5D",
  "5F"
];

const REKAM_ASUHAN_HD_INTENSIF: ReadonlyArray<string> = [
  "50",
  "5A",
  "5C",
  "5D",
  "5F"
];

const PTI_PICKUP_UNITS: ReadonlyArray<string> = [
  GADAR,
  IBS,
  HEMODIALISA,
  RB_OBSTETRI,
  "66"
];

const DASHBOARD_RALAN_EXCEPTIONS: ReadonlyArray<string> = ["34", "37", "38"];

const KLINIK_ANAK: ReadonlyArray<string> = ["06", "2A"];

const ASESMEN_KEMOTERAPI_CLINICS: ReadonlyArray<string> = ["2H", "38"];

const SKRINING_BUNUH_DIRI_EXCLUDED: ReadonlyArray<string> = [IBS, KUNTHI];

const KLINIK_REHAB_MEDIK = "11";
const KLINIK_MCU = "2Q";

const PEMULASARAN_JENAZAH = "39";
const KLINIK_FORENSIK = "2N";
const UNIT_PENGAWETAN_JENAZAH: ReadonlyArray<string> = [
  PEMULASARAN_JENAZAH,
  KLINIK_FORENSIK
];

const DAFTAR_PASIEN_TANPA_FILTER_DOKTER: ReadonlyArray<string> = [
  GADAR,
  KLINIK_MCU,
  KLINIK_REHAB_MEDIK,
  HEMODIALISA,
  KEMOTERAPI
];

const DAFTAR_PASIEN_UNIT_TANPA_TAB: ReadonlyArray<string> = [INSTALASI_GIZI];

const PAVILION_ANAK_GROUP_A: ReadonlyArray<string> = ["59", "60", "62", "68"];
const PAVILION_ANAK_GROUP_B: ReadonlyArray<string> = ["58", "71"];

function normalize(kode: string | null | undefined): string {
  return (kode ?? "").trim();
}

export function isGadar(kode: string | null | undefined): boolean {
  return normalize(kode) === GADAR;
}

export const isIGD = isGadar;

export function isRalan(kode: string | null | undefined): boolean {
  const k = normalize(kode);
  if (!k) return false;
  return k !== GADAR && k < "30";
}

export function isPenunjang(kode: string | null | undefined): boolean {
  const k = normalize(kode);
  return k >= "30" && k < "50";
}

export function isRanap(kode: string | null | undefined): boolean {
  const k = normalize(kode);
  return k >= "50" && k < "80";
}

export type PemeriksaanRouteName = "pemeriksaan" | "pemeriksaan-ranap";

export function pemeriksaanRouteName(
  kode: string | null | undefined,
  kodeAsal?: string | null | undefined
): PemeriksaanRouteName {
  const asal = normalize(kodeAsal) || normalize(kode);
  if (isGadar(asal)) return "pemeriksaan";
  return isRanap(kode) ? "pemeriksaan-ranap" : "pemeriksaan";
}

export type DaftarPasienRouteName =
  | "dashboard-poliklinik"
  | "daftar-pasien-igd"
  | "daftar-pasien"
  | "daftar-pasien-unit"
  | "daftar-pasien-pendaftaran"
  | "daftar-pasien-administrasi"
  | "dashboard-lain";

export function isDaftarPasienUnit(kode: string | null | undefined): boolean {
  const k = normalize(kode);
  return (
    k === LABORATORIUM ||
    k === RADIOLOGI ||
    DAFTAR_PASIEN_UNIT_TANPA_TAB.includes(k)
  );
}

export function isDaftarPasienUnitTanpaTab(
  kode: string | null | undefined
): boolean {
  return DAFTAR_PASIEN_UNIT_TANPA_TAB.includes(normalize(kode));
}

export function isRekamMedisPendaftaran(
  kode: string | null | undefined
): boolean {
  const k = normalize(kode);
  return k === REKAM_MEDIS || k === PENDAFTARAN;
}

export function isUnitRekamMedis(kode: string | null | undefined): boolean {
  return normalize(kode) === REKAM_MEDIS;
}

export function isUnitAdministrasi(kode: string | null | undefined): boolean {
  return normalize(kode) === ADMINISTRASI;
}

export function daftarPasienRouteName(
  kode: string | null | undefined
): DaftarPasienRouteName {
  const k = normalize(kode);
  if ((k >= "02" && k < "30") || ["37", "38", "39", "3A"].includes(k)) {
    return "dashboard-poliklinik";
  }
  if (isGadar(k)) return "daftar-pasien-igd";
  if ((k >= "50" && k < "80") || (k >= "33" && k < "37")) {
    return "daftar-pasien";
  }
  if (isDaftarPasienUnit(k)) return "daftar-pasien-unit";
  if (isRekamMedisPendaftaran(k)) return "daftar-pasien-pendaftaran";
  if (isUnitAdministrasi(k)) return "daftar-pasien-administrasi";
  return "dashboard-lain";
}

export function isAdmin(kode: string | null | undefined): boolean {
  const k = normalize(kode);
  return k >= "80";
}

export function isIntensif(kode: string | null | undefined): boolean {
  return UNIT_INTENSIF.includes(normalize(kode));
}

export function isKemoterapi(kode: string | null | undefined): boolean {
  return normalize(kode) === KEMOTERAPI;
}

export function isHemodialisa(kode: string | null | undefined): boolean {
  return normalize(kode) === HEMODIALISA;
}

export function isRekamAsuhanHdIntensif(
  kode: string | null | undefined
): boolean {
  return REKAM_ASUHAN_HD_INTENSIF.includes(normalize(kode));
}

export function isLaboratorium(kode: string | null | undefined): boolean {
  return normalize(kode) === LABORATORIUM;
}

export function isRadiologi(kode: string | null | undefined): boolean {
  return normalize(kode) === RADIOLOGI;
}

export function isUnitBankDarah(kode: string | null | undefined): boolean {
  return normalize(kode) === BANK_DARAH;
}

export function isLabRadiologi(kode: string | null | undefined): boolean {
  return isLaboratorium(kode) || isRadiologi(kode);
}

export function isInstalasiGizi(kode: string | null | undefined): boolean {
  return normalize(kode) === INSTALASI_GIZI;
}

export function isPemeriksaanRingkas(kode: string | null | undefined): boolean {
  return (
    isLabRadiologi(kode) ||
    isRekamMedisPendaftaran(kode) ||
    isInstalasiGizi(kode)
  );
}

export function isIBS(kode: string | null | undefined): boolean {
  return normalize(kode) === IBS;
}

export function isRuangBersalin(kode: string | null | undefined): boolean {
  return UNIT_BERSALIN.includes(normalize(kode));
}

/**
 * Hemodialisa & Kemoterapi adalah layanan rawat jalan terjadwal sehingga ikut
 * mengakses dashboard ralan meski kodenya >= '30'. Lihat AuthServiceProvider.php:43.
 */
export function canAccessDashboardRalan(
  kode: string | null | undefined
): boolean {
  const k = normalize(kode);
  return isRalan(k) || DASHBOARD_RALAN_EXCEPTIONS.includes(k);
}

export function isUnitPengawetanJenazah(
  kode: string | null | undefined
): boolean {
  return UNIT_PENGAWETAN_JENAZAH.includes(normalize(kode));
}

export function isKlinikAnak(kode: string | null | undefined): boolean {
  return KLINIK_ANAK.includes(normalize(kode));
}

export function isAsesmenKemoterapi(kode: string | null | undefined): boolean {
  return ASESMEN_KEMOTERAPI_CLINICS.includes(normalize(kode));
}

export function isSkriningBunuhDiriExcluded(
  ...kodes: Array<string | null | undefined>
): boolean {
  return kodes.some(k => SKRINING_BUNUH_DIRI_EXCLUDED.includes(normalize(k)));
}

export function isDaftarPasienTanpaFilterDokter(
  kode: string | null | undefined
): boolean {
  return DAFTAR_PASIEN_TANPA_FILTER_DOKTER.includes(normalize(kode));
}

export function getPavilionAnakSiblings(
  kode: string | null | undefined
): ReadonlyArray<string> {
  const k = normalize(kode);
  if (PAVILION_ANAK_GROUP_A.includes(k)) return PAVILION_ANAK_GROUP_A;
  if (PAVILION_ANAK_GROUP_B.includes(k)) return PAVILION_ANAK_GROUP_B;
  return [];
}

export function getUnitType(kode: string | null | undefined): UnitType {
  const k = normalize(kode);
  if (!k) return "unknown";
  if (isGadar(k)) return "gadar";
  if (isRalan(k)) return "ralan";
  if (isPenunjang(k)) return "penunjang";
  if (isRanap(k)) return "ranap";
  if (isAdmin(k)) return "admin";
  return "unknown";
}

export function isUnitJemputPasien(
  kode: string | null | undefined,
  units?: ReadonlyArray<string> | null
): boolean {
  const k = normalize(kode);
  if (!k) return false;
  const daftar = units && units.length ? units : PTI_PICKUP_UNITS;
  return daftar.map(u => normalize(u)).includes(k);
}

export function getPtiPickupUnits(): ReadonlyArray<string> {
  return PTI_PICKUP_UNITS;
}

export type PemeriksaanShell = "igd" | "ralan" | "ranap";

const NON_SEGMENT_UNITS: ReadonlyArray<string> = ["30", "31", "32", "39", "3A"];

export function isSegmentUnit(kode: string | null | undefined): boolean {
  const k = normalize(kode);
  if (!k) return false;
  return !NON_SEGMENT_UNITS.includes(k) && !isAdmin(k);
}

export function pemeriksaanShellForSegment(
  kode: string | null | undefined,
  opts?: { hasRanapSegment?: boolean }
): PemeriksaanShell {
  const k = normalize(kode);
  if (!k) return "ralan";
  if (isGadar(k)) return "igd";
  if (isRanap(k)) return "ranap";
  if (isRalan(k)) return "ralan";
  if (isIBS(k)) return opts && opts.hasRanapSegment ? "ranap" : "ralan";
  if (isHemodialisa(k) || isKemoterapi(k)) return "ralan";
  if (k >= "33" && k < "36") return "ranap";
  return "ralan";
}

export function pemeriksaanRouteNameForSegment(
  kode: string | null | undefined,
  opts?: { hasRanapSegment?: boolean }
): PemeriksaanRouteName {
  return pemeriksaanShellForSegment(kode, opts) === "ranap"
    ? "pemeriksaan-ranap"
    : "pemeriksaan";
}

export type PpiRekapCakupan = "ranap" | "ralan" | "semua";

export const PPI_RANAP_EKSTRA = ["01", "29"];

export const PPI_CAKUPAN_ITEMS = [
  { text: "Ranap (Bangsal, IGD, PPRI)", value: "ranap" },
  { text: "Ralan / Poliklinik", value: "ralan" },
  { text: "Semua Unit", value: "semua" }
];

export function ppiCakupanPicker(
  cakupan: PpiRekapCakupan
): { kategori: string[]; include: string[]; exclude: string[] } {
  if (cakupan === "semua") {
    return { kategori: [], include: [], exclude: [] };
  }
  if (cakupan === "ralan") {
    return { kategori: ["ralan"], include: [], exclude: PPI_RANAP_EKSTRA };
  }
  return { kategori: ["ranap"], include: PPI_RANAP_EKSTRA, exclude: [] };
}

